import { computed, ref, type ComputedRef, type Ref } from 'vue';
import type { FilterDescriptor } from '@progress/kendo-data-query';
import type { Page } from '@/composables/usePagedList';
import { useSearchableList } from '@/composables/useSearchableList';

export interface BrowserListConfig<T> {
    predicates: ComputedRef<FilterDescriptor[]>;
    filterRefs: Ref<{ stringPartial: string; [key: string]: any }>;
    // Resolves to nothing when the request fails: the list is then left as it was.
    fetchFn: (url: string | null, predicates: FilterDescriptor[]) => Promise<Page<T> | undefined>;
}

/**
 * The list behind a browser drawer: asked for when the drawer opens, searched from the drawer's
 * own search box, let go when the drawer closes. What a browser does with a row — add it, confirm
 * it — is its own (useElementBrowser, useWorldBrowser).
 */
export function useBrowserList<T>(config: BrowserListConfig<T>) {
    const selectedElement = ref<T | null>(null) as Ref<T | null>;
    const isOpen = ref(false);

    // The list itself — latest request wins, paging, refresh, and a search that waits for the user
    // to pause — is useSearchableList's. A browser's host binds its @search straight to onSearch.
    // (No predicate watcher to square the search with, unlike useElementGrid: a host calls fetch()
    // by hand after its own filter writes.)
    const { dataItems, loading, metadata, links, fetch, refresh, onNextPage, onPreviousPage, onSearch, cancelSearch } =
        useSearchableList<T>({
            keyword: computed({
                get: () => config.filterRefs.value.stringPartial,
                set: (value) => { config.filterRefs.value.stringPartial = value; },
            }),
            fetchPage: (url) => config.fetchFn(url, config.predicates.value),
            // A changed text is a new list: what was picked from the old one no longer counts.
            onTyped: () => { selectedElement.value = null; },
        });

    const open = async () => {
        isOpen.value = true;
        // The browser opens on an unfiltered first page, under a search box its drawer empties as
        // it shows. close() clears the keyword as well, but opening cannot count on it having
        // run: a browser opened again while it is still closing is never told that it closed
        // (useOffcanvas drops that report for a panel already on its way back in). The slot can
        // also be a shared one, which other surfaces' searches write.
        config.filterRefs.value.stringPartial = '';
        await fetch();
    };

    const close = () => {
        // A search still waiting on its timer must not fire into a closed browser, whose text
        // has just been reset.
        cancelSearch();
        isOpen.value = false;
        selectedElement.value = null;
        dataItems.value = [];
        config.filterRefs.value.stringPartial = '';
    };

    return {
        dataItems,
        selectedElement,
        isOpen,
        loading,
        metadata,
        links,
        fetch,
        open,
        close,
        onSearch,
        onNextPage,
        onPreviousPage,
        refresh,
    };
}
